import axios from 'axios';
import supabase, { getSessionSafe } from './supabase-client';
import clientEnv from '../env';
import { logApiRequest, logApiResponse, logApiError, createTimingMetadata, calculateTiming } from './api-logger';
import { toUserFacingMessage } from './user-facing-api-error';
import { isOnboardingRoute } from './onboarding-route';
/** Attach safe UI copy for axios consumers (Redux catch blocks). Idempotent. */
function attachUserFacingMessageToAxiosError(error) {
    if (!error || typeof error !== 'object')
        return;
    const e = error;
    if (typeof e.userFacingMessage === 'string' && e.userFacingMessage.trim())
        return;
    try {
        e.userFacingMessage = toUserFacingMessage(error);
    }
    catch {
        // ignore
    }
}
function rejectWithUserFacing(error) {
    attachUserFacingMessageToAxiosError(error);
    return Promise.reject(error);
}
// Dynamically determine API base URL with normalization to ensure `/api` prefix exists
// Priority:
// 1. Explicit backend URL provided via REACT_APP_API_SERVER or REACT_APP_API_URL
// 2. If running in the browser and no explicit URL, use a relative `/api` path
// 3. For tests only - use localhost fallback (tests set REACT_APP_API_URL in setupTests.ts)
function normalizeBaseURL(rawBaseUrl) {
    if (!rawBaseUrl) {
        return '/api';
    }
    // Remove trailing slashes
    rawBaseUrl = rawBaseUrl.replace(/\/+$/, '');
    // If a relative path is provided (starts with /)
    if (rawBaseUrl.startsWith('/')) {
        // Check if it already has /api
        if (rawBaseUrl === '/api' || rawBaseUrl.startsWith('/api/')) {
            return rawBaseUrl;
        }
        // Ensure it ends with /api
        return `${rawBaseUrl}/api`;
    }
    // If absolute URL, parse and normalize
    try {
        const url = new URL(rawBaseUrl);
        let pathname = url.pathname || '/';
        // Remove trailing slashes from pathname
        pathname = pathname.replace(/\/+$/, '') || '/';
        // Check if pathname already contains /api
        if (pathname === '/api' || pathname.startsWith('/api/')) {
            // Already has /api, but ensure no double /api/api patterns
            // Handle multiple occurrences: /api/api, /api/api/api, etc.
            pathname = pathname.replace(/\/api\/api(\/api)*/g, '/api');
            url.pathname = pathname;
            return url.toString();
        }
        // Add /api to pathname
        url.pathname = `${pathname === '/' ? '' : pathname}/api`;
        return url.toString();
    }
    catch {
        // Fallback: if it looks like it might already have /api, return as-is
        // Otherwise append /api
        if (rawBaseUrl.includes('/api')) {
            // Remove any double /api/api patterns (handles /api/api, /api/api/api, etc.)
            // More explicit pattern: match /api/api followed by zero or more /api sequences
            return rawBaseUrl.replace(/\/api\/api(\/api)*/g, '/api');
        }
        return `${rawBaseUrl}/api`;
    }
}
// Export normalizeBaseURL for reuse in other modules
export { normalizeBaseURL };
// Priority: REACT_APP_API_SERVER > REACT_APP_API_URL > localhost fallback (dev only)
// NOTE: RENDER_EXTERNAL_URL is NOT used here as it points to the frontend, not the API server
const rawBaseURL = clientEnv.REACT_APP_API_SERVER || clientEnv.REACT_APP_API_URL;
// In production, require API URL to be set
if (clientEnv.NODE_ENV === 'production' && !rawBaseURL) {
    throw new Error('REACT_APP_API_URL or REACT_APP_API_SERVER must be set in production. ' +
        'Cannot use localhost fallback in production environment.');
}
// NO FALLBACKS - REACT_APP_API_URL must be set in environment
// Tests should set REACT_APP_API_URL in setupTests.ts
if (!rawBaseURL) {
    throw new Error('REACT_APP_API_URL must be set in environment variables. ' +
        'No fallback values are allowed. Set it in your .env file or test configuration.');
}
const finalBaseURL = rawBaseURL;
// In development, use relative path for webpack proxy
// In production, use full URL with /api
const baseURL = typeof window !== 'undefined' && clientEnv.NODE_ENV === 'development'
    ? '/api' // Use relative path for webpack proxy
    : normalizeBaseURL(finalBaseURL || '/api');
const axiosInstance = axios.create({
    baseURL,
    timeout: 60000, // Increased timeout to 60 seconds for complex operations
    // Send session cookie (mypace.sid) on cross-origin API calls; required for CSRF validation after chained POSTs (e.g. stripe intent then subscription).
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
});
// CSRF token cache
let csrfTokenCache = {
    token: null,
    timestamp: 0
};
const CSRF_TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CSRF_TOKEN_REFRESH_THRESHOLD = 0.8; // Refresh when 80% of cache duration has passed
const CSRF_RETRY_DELAY = 200; // Delay before retrying after CSRF token refresh (ms)
// Mutex to prevent concurrent token fetches (prevents multiple sessions from being created)
let tokenFetchPromise = null;
// Session cache - reduces redundant getSession() calls on every request
const SESSION_CACHE_BUFFER_MS = 30 * 1000; // Consider expired 30s before JWT expiry
let sessionCache = {
    session: null,
    expiresAt: 0
};
let sessionFetchPromise = null;
/** Single-flight Supabase refresh — parallel axios/socket callers must not storm `/auth/v1/token` (429). */
let refreshSessionPromise = null;
function isRefreshRateLimited(error) {
    if (!error)
        return false;
    if (error.status === 429)
        return true;
    const code = String(error.code ?? '').toLowerCase();
    if (code.includes('rate') || code === 'over_request_rate_limit')
        return true;
    const msg = String(error.message ?? '').toLowerCase();
    return msg.includes('rate limit') || msg.includes('too many requests');
}
/**
 * Deduplicates `supabase.auth.refreshSession()` across the app (axios interceptors + socket).
 * Updates `sessionCache` and localStorage on success. Retries with backoff on 429 rate limit.
 */
export async function refreshSessionSingleFlight() {
    if (refreshSessionPromise) {
        return refreshSessionPromise;
    }
    const maxAttempts = 3;
    refreshSessionPromise = (async () => {
        let last = null;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const result = await supabase.auth.refreshSession();
            last = result;
            const err = result.error;
            if (!err && result.data?.session) {
                const session = result.data.session;
                const newExpiresAt = JSON.parse(atob(session.access_token.split('.')[1])).exp * 1000;
                sessionCache = {
                    session: { access_token: session.access_token, refresh_token: session.refresh_token },
                    expiresAt: newExpiresAt
                };
                localStorage.setItem('access_token', session.access_token);
                localStorage.setItem('refresh_token', session.refresh_token);
                return result;
            }
            if (err && isRefreshRateLimited(err) && attempt < maxAttempts - 1) {
                const delayMs = 700 * Math.pow(2, attempt);
                await new Promise((r) => setTimeout(r, delayMs));
                continue;
            }
            return result;
        }
        return last;
    })();
    try {
        return await refreshSessionPromise;
    }
    finally {
        refreshSessionPromise = null;
    }
}
// Function to clear session cache (call on logout)
export const clearSessionCache = () => {
    sessionCache = { session: null, expiresAt: 0 };
    sessionFetchPromise = null;
    refreshSessionPromise = null;
};
/**
 * Pre-populate session cache from Redux session (e.g. login.fulfilled or persist rehydration).
 * Fixes race where first API requests hit before supabase.auth.getSession() resolves.
 * Call before triggering data fetches when session is available in Redux.
 */
export const setSessionFromLogin = (session) => {
    try {
        const tokenData = JSON.parse(atob(session.access_token.split('.')[1]));
        const expiresAt = tokenData.exp * 1000;
        if (expiresAt > Date.now()) {
            sessionCache = {
                session: { access_token: session.access_token, refresh_token: session.refresh_token },
                expiresAt
            };
        }
    }
    catch {
        // Invalid token, don't cache
    }
};
// Get session with caching - returns cached session if still valid (30s buffer before expiry)
const getCachedSession = async () => {
    const now = Date.now();
    // Return cached session if still valid (not expired, with buffer)
    if (sessionCache.session && sessionCache.expiresAt > now + SESSION_CACHE_BUFFER_MS) {
        return sessionCache.session;
    }
    // If a fetch is in progress, wait for it
    if (sessionFetchPromise) {
        try {
            const session = await sessionFetchPromise;
            return session;
        }
        catch {
            sessionFetchPromise = null;
        }
    }
    // Start new fetch with mutex.
    // Uses getSessionSafe() which races supabase.auth.getSession() against a 3s
    // timeout and falls back to localStorage tokens, preventing navigator.locks
    // contention from wedging every API call on app boot.
    sessionFetchPromise = (async () => {
        try {
            const session = await getSessionSafe();
            if (session?.access_token) {
                try {
                    const tokenData = JSON.parse(atob(session.access_token.split('.')[1]));
                    const expiresAt = tokenData.exp * 1000;
                    const nowInner = Date.now();
                    // Only cache if not already expired (interceptor will refresh if near expiry)
                    if (expiresAt > nowInner) {
                        sessionCache = {
                            session: { access_token: session.access_token, refresh_token: session.refresh_token },
                            expiresAt
                        };
                    }
                }
                catch {
                    // Malformed token — skip caching but still return session so the
                    // interceptor can attempt a refresh on 401.
                }
                return { access_token: session.access_token, refresh_token: session.refresh_token };
            }
            sessionCache = { session: null, expiresAt: 0 };
            return null;
        }
        catch (error) {
            // getSessionSafe() never throws, but defend in depth.
            sessionCache = { session: null, expiresAt: 0 };
            console.warn('[axios-auth] getCachedSession unexpected error, treating as no session:', error);
            return null;
        }
        finally {
            sessionFetchPromise = null;
        }
    })();
    return sessionFetchPromise;
};
// Function to clear CSRF token cache (useful for logout or token refresh)
export const clearCSRFTokenCache = () => {
    csrfTokenCache = {
        token: null,
        timestamp: 0
    };
    // Don't clear tokenFetchPromise here - let it complete naturally
    // Clearing it while in progress could cause race conditions where
    // other requests waiting on the promise would fail
    // The finally block in getCSRFToken will clear it when the fetch completes
};
/**
 * Proactively fetch CSRF token and establish session (mypace.sid cookie) on app load.
 * Call this early (e.g., in App.tsx useEffect) so session exists before first API request.
 * Prevents CSRF 403 when first user action is a state-changing request (e.g., create quiz template).
 */
export const prefetchCSRFToken = () => getCSRFToken();
// Helper function to construct CSRF token URL
const getCSRFTokenUrl = () => {
    // Construct CSRF token URL correctly
    // If baseURL is absolute (production), use it directly
    // If baseURL is relative (development), use it as-is
    if (baseURL.startsWith('http://') || baseURL.startsWith('https://')) {
        // Absolute URL - ensure it has /api/security/csrf-token
        const url = new URL(baseURL);
        if (url.pathname.endsWith('/api')) {
            return `${url.origin}/api/security/csrf-token`;
        }
        else if (url.pathname.includes('/api/')) {
            return `${url.origin}/api/security/csrf-token`;
        }
        else {
            return `${baseURL}/security/csrf-token`;
        }
    }
    else {
        // Relative URL (development) - baseURL is '/api'
        return '/api/security/csrf-token';
    }
};
// Helper function to get CSRF token with caching, proactive refresh, and mutex protection
const getCSRFToken = async () => {
    const now = Date.now();
    const cacheAge = now - csrfTokenCache.timestamp;
    const refreshThreshold = CSRF_TOKEN_CACHE_DURATION * CSRF_TOKEN_REFRESH_THRESHOLD;
    // Return cached token if it's still valid and not near expiration
    if (csrfTokenCache.token && cacheAge < refreshThreshold) {
        return csrfTokenCache.token;
    }
    // If token is near expiration (80% of cache duration), refresh proactively
    // This prevents token expiration during long sessions
    if (csrfTokenCache.token && cacheAge >= refreshThreshold && cacheAge < CSRF_TOKEN_CACHE_DURATION) {
        // Token is still valid but near expiration - refresh in background
        // Continue with current token but refresh for next time
        refreshCSRFTokenInBackground();
        return csrfTokenCache.token;
    }
    // If a fetch is already in progress, wait for it instead of creating a new one
    // This prevents multiple concurrent fetches that could create different sessions
    if (tokenFetchPromise) {
        if (clientEnv.NODE_ENV === 'development') {
            console.debug('CSRF token fetch already in progress, waiting for existing request');
        }
        try {
            // Wait for the in-progress fetch, but handle rejections gracefully
            const token = await tokenFetchPromise;
            return token;
        }
        catch (error) {
            // If the in-progress fetch failed, continue to start a new one below
            // This prevents all requests from failing if one fetch fails
            if (clientEnv.NODE_ENV === 'development') {
                console.debug('In-progress CSRF token fetch failed, starting new fetch:', error);
            }
            // Clear the failed promise so we can start a new one
            tokenFetchPromise = null;
        }
    }
    // Start new fetch with mutex protection
    const csrfUrl = getCSRFTokenUrl();
    tokenFetchPromise = (async () => {
        try {
            const response = await fetch(csrfUrl, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                // Verify we got a valid token
                if (!data.token || typeof data.token !== 'string') {
                    console.error('Invalid CSRF token response:', data);
                    return null;
                }
                // Cache the token
                csrfTokenCache = {
                    token: data.token,
                    timestamp: now
                };
                // Log in development for debugging
                if (clientEnv.NODE_ENV === 'development') {
                    console.debug('CSRF token fetched successfully', {
                        tokenLength: data.token.length,
                        rotated: data.rotated || false,
                        sessionSaved: data.sessionSaved !== false, // Default to true if not specified
                        sessionId: data.sessionId || 'not-provided'
                    });
                }
                return data.token;
            }
            else {
                // Non-OK response - log for debugging
                console.error('CSRF token endpoint returned non-OK status:', {
                    status: response.status,
                    statusText: response.statusText,
                    url: csrfUrl
                });
                return null;
            }
        }
        catch (error) {
            // Better error detection for CORS and network errors
            const errorMessage = String(error?.message || '');
            const errorName = String(error?.name || '');
            const isNetworkError = error?.name === 'TypeError' ||
                error?.message?.includes('Load failed') ||
                error?.message?.includes('Failed to fetch') ||
                error?.code === 'ERR_NETWORK' ||
                error?.code === 'ERR_CORS';
            const isCORSError = errorMessage.includes('CORS') ||
                errorMessage.includes('Access-Control') ||
                errorMessage.includes('blocked by CORS policy') ||
                error?.code === 'ERR_CORS';
            // Log CORS errors specifically as they indicate a configuration issue
            if (isCORSError) {
                console.error('CORS error fetching CSRF token:', {
                    url: csrfUrl,
                    error: errorMessage,
                    code: error?.code,
                    name: errorName,
                    message: 'CORS preflight request failed. Check server CORS configuration.'
                });
            }
            else if (!isNetworkError) {
                // Log non-network errors (they indicate actual problems)
                console.warn('Failed to get CSRF token:', {
                    url: csrfUrl,
                    error: errorMessage,
                    code: error?.code
                });
            }
            // Network errors (offline, timeout) are expected and don't need logging
            // If we have a cached token, use it even if it's expired
            // This allows the app to continue functioning
            if (csrfTokenCache.token) {
                console.debug('Using cached CSRF token due to fetch error');
                return csrfTokenCache.token;
            }
            return null;
        }
        finally {
            // Clear the promise after fetch completes (success or failure)
            // This allows future fetches to proceed
            tokenFetchPromise = null;
        }
    })();
    return tokenFetchPromise;
};
// Background refresh function to proactively refresh CSRF token
const refreshCSRFTokenInBackground = async () => {
    try {
        // If a fetch is in progress, wait for it and use that token instead of starting a new fetch
        // This prevents multiple concurrent fetches and ensures we use the latest token
        if (tokenFetchPromise) {
            try {
                const token = await tokenFetchPromise;
                if (token) {
                    // Token already fetched by in-progress request, no need to fetch again
                    if (clientEnv.NODE_ENV === 'development') {
                        console.debug('Background CSRF refresh: Using token from in-progress fetch');
                    }
                    return;
                }
            }
            catch (fetchError) {
                // In-progress fetch failed, continue to start a new fetch below
                if (clientEnv.NODE_ENV === 'development') {
                    console.debug('Background CSRF refresh: In-progress fetch failed, starting new fetch');
                }
            }
        }
        const csrfUrl = getCSRFTokenUrl();
        const response = await fetch(csrfUrl, {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
            },
        });
        if (response.ok) {
            const data = await response.json();
            // Update cache with new token
            csrfTokenCache = {
                token: data.token,
                timestamp: Date.now()
            };
        }
    }
    catch (error) {
        // Silently fail - we'll refresh on next request
        console.debug('Background CSRF token refresh failed:', error);
    }
};
// Request interceptor to add auth token
axiosInstance.interceptors.request.use(async (config) => {
    // Add timing metadata for logging
    config.metadata = createTimingMetadata();
    // Log request in development
    logApiRequest(config);
    // Don't modify the URL - let axios handle the baseURL properly
    // Skip auth interceptor for Supabase requests to avoid conflicts
    if (config.url?.includes('supabase.co') || config.url?.includes('supabase')) {
        return config;
    }
    // Only set baseURL if the request URL is not a full URL (doesn't start with http:// or https://)
    if (!config.url?.startsWith('http://') && !config.url?.startsWith('https://')) {
        // Ensure the URL doesn't start with /api/ since baseURL already includes /api
        if (config.url?.startsWith('/api/')) {
            // Remove /api prefix to prevent double /api/api
            config.url = config.url.replace(/^\/api/, '');
        }
        config.baseURL = baseURL;
    }
    // For multipart/form-data (file uploads), don't set Content-Type as it needs 
    // to include the boundary parameter automatically
    if (config.url?.includes('upload')) {
        delete config.headers['Content-Type'];
    }
    else {
        // For all other requests ensure JSON content type
        config.headers['Content-Type'] = 'application/json';
    }
    // Always ensure Accept header for JSON
    config.headers['Accept'] = 'application/json';
    // Skip CSRF token for the CSRF token endpoint itself (it's in the skip list on server)
    const isCSRFTokenEndpoint = config.url?.includes('/security/csrf-token');
    // Get CSRF token for all requests (server requires it for all API routes)
    // Skip for CSRF token endpoint to avoid circular dependency
    const csrfToken = isCSRFTokenEndpoint ? null : await getCSRFToken();
    if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
    }
    else {
        // Only warn if it's not a network/CORS error (those are logged above)
        const isNetworkError = !csrfTokenCache.token &&
            (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine);
        if (!isNetworkError) {
            console.warn('Could not obtain CSRF token - request will proceed without it (server may reject)');
        }
    }
    try {
        // Get session from cache (reduces redundant getSession() calls on every request)
        const cachedSession = await getCachedSession();
        if (!cachedSession && !config.url?.includes('/security/csrf-token')) {
            console.warn('[axios-auth] No cached session for:', config.url);
        }
        if (cachedSession) {
            const tokenData = JSON.parse(atob(cachedSession.access_token.split('.')[1]));
            const expiresAt = tokenData.exp * 1000; // Convert to milliseconds
            const isExpired = Date.now() >= expiresAt;
            if (isExpired) {
                console.warn('Token is expired, attempting to refresh...');
                const { data, error } = await refreshSessionSingleFlight();
                if (error) {
                    console.error('Failed to refresh token:', error);
                    const fallbackToken = localStorage.getItem('access_token');
                    if (fallbackToken) {
                        config.headers['Authorization'] = `Bearer ${fallbackToken}`;
                        return config;
                    }
                    throw new Error('Failed to refresh authentication token');
                }
                if (data?.session) {
                    config.headers['Authorization'] = `Bearer ${data.session.access_token}`;
                    return config;
                }
            }
            else {
                // Token is valid, use cached session
                localStorage.setItem('access_token', cachedSession.access_token);
                localStorage.setItem('refresh_token', cachedSession.refresh_token);
                config.headers['Authorization'] = `Bearer ${cachedSession.access_token}`;
                return config;
            }
        }
        else {
            console.warn('[axios-auth] No Supabase session, checking localStorage fallback');
            // Try localStorage as a fallback
            const token = localStorage.getItem('access_token');
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
                return config;
            }
            // For public endpoints, don't throw auth error - just continue without auth
            const isPublicEndpoint = config.url && (config.url.includes('/subscriptions/tiers') ||
                config.url.includes('/security/csrf-token') ||
                config.url.includes('/health') ||
                config.url.includes('/location/timezone') ||
                config.url.includes('/location/timezone-by-ip'));
            if (isPublicEndpoint) {
                // Public endpoint - continue without auth
                return config;
            }
            // Don't redirect automatically - let the component handle it
            // This prevents infinite redirect loops
            console.warn('No auth token available, user is not authenticated');
            // Create a proper Axios error structure to maintain consistency
            const authError = new Error('Authentication required');
            authError.response = {
                status: 401,
                data: {
                    message: 'Authentication required',
                    code: 'AUTH_REQUIRED'
                }
            };
            authError.isAxiosError = true;
            throw authError;
        }
    }
    catch (error) {
        console.error('Error retrieving auth token:', error);
        // Try localStorage as a last resort
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
            return config;
        }
        // For auth endpoints or public endpoints, continue without auth
        const isPublicEndpoint = config.url && (config.url.includes('login') ||
            config.url.includes('auth') ||
            config.url.includes('public') ||
            config.url.includes('debug') ||
            config.url.includes('subscription') ||
            config.url.includes('payment') ||
            config.url.includes('/security/csrf-token') ||
            config.url.includes('/health') ||
            config.url.includes('/location/timezone') ||
            config.url.includes('/location/timezone-by-ip'));
        if (isPublicEndpoint) {
            return config;
        }
        // Don't redirect automatically - let the component handle it
        // This prevents infinite redirect loops
        console.warn('Authentication error - component should handle redirect');
        // Create a proper Axios error structure to maintain consistency
        const authError = new Error('Authentication required');
        authError.response = {
            status: 401,
            data: {
                message: 'Authentication required',
                code: 'AUTH_REQUIRED'
            }
        };
        authError.isAxiosError = true;
        throw authError;
    }
    return config;
}, (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
});
// Response interceptor to handle auth errors
axiosInstance.interceptors.response.use((response) => {
    // Calculate timing
    if (response.config?.metadata?.startTime) {
        response.config.metadata.timing = calculateTiming(response.config.metadata.startTime);
    }
    // Log response in development
    logApiResponse(response);
    return response;
}, async (error) => {
    const originalRequest = error.config || {};
    // If no config, the error was thrown from the request interceptor (e.g. no auth session)
    if (!error.config) {
        return rejectWithUserFacing(error);
    }
    // Intentional cancellation (AbortController, rapid date changes, navigation) - reject silently
    const isCanceled = error.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError';
    if (isCanceled) {
        return rejectWithUserFacing(error);
    }
    // Calculate timing for errors
    if (originalRequest?.metadata?.startTime) {
        originalRequest.metadata.timing = calculateTiming(originalRequest.metadata.startTime);
    }
    // Log error in development
    logApiError(error, {
        retry: originalRequest._retry,
        csrfRetry: originalRequest._csrfRetry
    });
    // Improved error logging with better network error detection
    // Network errors can have various forms:
    // 1. No response object (request failed before reaching server)
    // 2. ERR_NETWORK code
    // 3. "Network Error" in message
    // 4. Request timeout without response
    // 5. Check both error.message and error.response?.data?.message
    // 6. Check error name for AxiosError with network issues
    const errorMessage = String(error.message || error.response?.data?.message || '');
    const errorName = String(error.name || '');
    const isNetworkError = ((!error.response && error.request) || // Request made but no response
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ETIMEDOUT' ||
        errorName === 'NetworkError' ||
        (errorMessage && (errorMessage.includes('Network Error') ||
            errorMessage.includes('ERR_NETWORK') ||
            errorMessage.toLowerCase().includes('network') ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('ECONNREFUSED') ||
            errorMessage.includes('ENOTFOUND') ||
            errorMessage.includes('ERR_INTERNET_DISCONNECTED'))));
    // Check if device is offline
    const isOffline = typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine;
    // Retry logic for transient network errors and server errors
    const isRetryableStatus = error.response?.status && [502, 503, 504].includes(error.response.status);
    const shouldRetry = (isNetworkError || isRetryableStatus) && !isOffline && !originalRequest._networkRetry;
    if (shouldRetry) {
        // Only retry GET requests and non-destructive operations
        const isRetryableMethod = ['GET', 'HEAD', 'OPTIONS'].includes(originalRequest.method?.toUpperCase() || '');
        const isRetryableError = (error.code === 'ERR_NETWORK' ||
            error.code === 'ETIMEDOUT' ||
            error.code === 'ECONNABORTED' ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('Failed to fetch') ||
            isRetryableStatus // 502, 503, 504 are transient server errors
        );
        if (isRetryableMethod && isRetryableError) {
            originalRequest._networkRetry = true;
            originalRequest._networkRetryCount = (originalRequest._networkRetryCount || 0) + 1;
            // Only retry up to 2 times for network/server errors
            if (originalRequest._networkRetryCount <= 2) {
                const retryDelay = originalRequest._networkRetryCount * 1000; // Exponential backoff: 1s, 2s
                const errorType = isRetryableStatus ? `Server error (${error.response.status})` : 'Network error';
                console.warn(`${errorType} detected, retrying in ${retryDelay}ms (attempt ${originalRequest._networkRetryCount}/2):`, {
                    url: originalRequest.url,
                    code: error.code,
                    status: error.response?.status
                });
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(axiosInstance(originalRequest));
                    }, retryDelay);
                });
            }
        }
    }
    // Handle network errors and server errors
    if (isNetworkError || isRetryableStatus) {
        // For network errors, use warning level and check offline status
        if (isOffline) {
            console.warn('Network Error (offline):', {
                url: originalRequest.url,
                fullUrl: `${originalRequest.baseURL}${originalRequest.url}`,
                message: 'Device is offline. Please check your connection.',
                code: error.code,
                status: error.response?.status,
                retryAttempted: originalRequest._networkRetry || false
            });
        }
        else {
            const errorType = isRetryableStatus ? 'Server Error' : 'Network Error';
            const errorMessage = isRetryableStatus
                ? `Server temporarily unavailable (${error.response?.status}). Retrying...`
                : 'Network request failed. Please check your connection.';
            console.warn(`${errorType}:`, {
                url: originalRequest.url,
                fullUrl: `${originalRequest.baseURL}${originalRequest.url}`,
                message: errorMessage,
                code: error.code,
                status: error.response?.status,
                retryAttempted: originalRequest._networkRetry || false,
                retryCount: originalRequest._networkRetryCount || 0
            });
        }
    }
    else {
        console.error('API Error:', {
            url: originalRequest.url,
            fullUrl: `${originalRequest.baseURL}${originalRequest.url}`,
            status: error.response?.status,
            message: error.response?.data?.message || error.message,
            code: error.code
        });
    }
    // Suppress expected 401 errors for public endpoints when user is not authenticated
    const isPublicEndpoint = originalRequest?.url && (originalRequest.url.includes('/subscriptions/tiers') ||
        originalRequest.url.includes('/security/csrf-token') ||
        originalRequest.url.includes('/health') ||
        originalRequest.url.includes('/location/timezone') ||
        originalRequest.url.includes('/location/timezone-by-ip'));
    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
        // For public endpoints, don't retry - just return the error
        if (isPublicEndpoint) {
            return rejectWithUserFacing(error);
        }
        originalRequest._retry = true;
        try {
            const { data, error: refreshError } = await refreshSessionSingleFlight();
            if (!refreshError && data?.session) {
                originalRequest.headers['Authorization'] = `Bearer ${data.session.access_token}`;
                return axiosInstance(originalRequest);
            }
            if (refreshError) {
                console.warn('Session refresh failed:', {
                    status: refreshError.status,
                    code: refreshError.code,
                    message: refreshError.message
                });
            }
            else if (!data?.session) {
                console.warn('Session refresh completed but no session was returned');
            }
        }
        catch (refreshError) {
            console.error('Error refreshing auth token:', refreshError);
            await supabase.auth.signOut();
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            // Don't redirect automatically - let the component handle it
            console.warn('Session refresh failed - component should handle redirect');
        }
    }
    // If error is 403 Forbidden, check if it's a CSRF issue or subscription required
    if (error.response?.status === 403) {
        const errorData = error.response.data || {};
        const errorMessage = errorData.message || '';
        const errorCode = errorData.code || '';
        // Check for subscription required errors
        const isSubscriptionRequired = errorCode === 'SUBSCRIPTION_REQUIRED' ||
            errorMessage.toLowerCase().includes('subscription required') ||
            errorMessage.toLowerCase().includes('active subscription required') ||
            errorData.upgrade_required === true;
        if (isSubscriptionRequired) {
            // Emit custom event for subscription required errors (not during onboarding setup)
            if (typeof window !== 'undefined' && !isOnboardingRoute()) {
                window.dispatchEvent(new CustomEvent('subscription-required', {
                    detail: {
                        message: errorMessage || 'An active subscription is required to access this feature.',
                        code: errorCode,
                        path: errorData.path || originalRequest?.url,
                        upgradeRequired: errorData.upgrade_required || true
                    }
                }));
            }
            // Enhance error object with subscription-specific information
            error.subscriptionRequired = true;
            error.upgradePath = '/tutor-dashboard/onboarding'; // Default upgrade path for tutors
            console.warn('Subscription required:', {
                code: errorCode,
                message: errorMessage,
                path: errorData.path || originalRequest?.url,
                upgradeRequired: errorData.upgrade_required
            });
            // Don't retry subscription required errors
            return rejectWithUserFacing(error);
        }
        // Check for CSRF errors by message or code (more explicit checking)
        const isCSRFError = errorCode === 'CSRF_INVALID' ||
            errorCode === 'CSRF_SESSION_MISSING' ||
            errorCode === 'CSRF_ERROR' ||
            errorMessage.toLowerCase().includes('csrf') ||
            errorMessage.toLowerCase().includes('cross-site request');
        if (isCSRFError) {
            console.warn('CSRF protection blocked the request:', {
                code: errorCode,
                message: errorMessage,
                path: originalRequest.url,
                method: originalRequest.method
            });
            // Try to get a fresh CSRF token and retry the request
            // Only retry once to prevent infinite loops
            if (!originalRequest._csrfRetry) {
                originalRequest._csrfRetry = true;
                // Clear the cached token but NOT the in-progress fetch promise
                // Clearing the promise could cause race conditions with other requests
                // that are waiting on it. The promise will complete naturally.
                csrfTokenCache = {
                    token: null,
                    timestamp: 0
                };
                // Also clear the CSRF cookie if it exists (to ensure we get a fresh one)
                // The cookie name is 'csrf-token' (from CSRF protection middleware)
                // Clear for all possible paths and domains to ensure it's removed regardless of how it was set
                if (typeof document !== 'undefined') {
                    const paths = ['/', '/api', '/api/'];
                    const hostname = window.location.hostname;
                    const domains = ['', hostname, `.${hostname}`];
                    const isSecure = window.location.protocol === 'https:';
                    // Try clearing with all combinations of path and domain
                    paths.forEach(path => {
                        domains.forEach(domain => {
                            const domainPart = domain ? `; domain=${domain}` : '';
                            const securePart = isSecure ? '; Secure' : '';
                            // Clear with SameSite=Lax
                            document.cookie = `csrf-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domainPart}; SameSite=Lax${securePart}`;
                            // Also try with SameSite=Strict (in case it was set that way)
                            document.cookie = `csrf-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domainPart}; SameSite=Strict${securePart}`;
                            // Also try without SameSite attribute
                            document.cookie = `csrf-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domainPart}${securePart}`;
                        });
                    });
                    if (clientEnv.NODE_ENV === 'development') {
                        console.debug('Cleared CSRF cookie for all paths and domains');
                    }
                }
                // Fetch a fresh CSRF token - this will create/update the session
                // The mutex in getCSRFToken will prevent concurrent fetches
                try {
                    const freshToken = await getCSRFToken();
                    if (freshToken && typeof freshToken === 'string') {
                        // Verify session cookie exists before retrying
                        // If session cookie is missing, the retry will likely fail again
                        let hasSessionCookie = false;
                        let sessionCookieValue = null;
                        let cookieCheckAttempts = 0;
                        const MAX_COOKIE_CHECK_ATTEMPTS = 5;
                        const COOKIE_CHECK_DELAY = 200; // 200ms between checks
                        if (typeof document !== 'undefined') {
                            // Wait for session cookie to be set (may take a moment after token fetch)
                            while (!hasSessionCookie && cookieCheckAttempts < MAX_COOKIE_CHECK_ATTEMPTS) {
                                const cookies = document.cookie.split(';').map(c => c.trim());
                                const sessionCookie = cookies.find(c => c.startsWith('mypace.sid='));
                                if (sessionCookie) {
                                    hasSessionCookie = true;
                                    sessionCookieValue = sessionCookie.substring('mypace.sid='.length);
                                    break;
                                }
                                cookieCheckAttempts++;
                                if (cookieCheckAttempts < MAX_COOKIE_CHECK_ATTEMPTS) {
                                    // Wait a bit before checking again
                                    await new Promise(resolve => setTimeout(resolve, COOKIE_CHECK_DELAY));
                                }
                            }
                            if (!hasSessionCookie) {
                                console.warn('Session cookie missing after CSRF token fetch and wait period', {
                                    path: originalRequest.url,
                                    cookieCheckAttempts,
                                    allCookies: typeof document !== 'undefined' ? document.cookie.split(';').length : 0,
                                    cookieDomain: typeof window !== 'undefined' ? window.location.hostname : 'unknown'
                                });
                            }
                            else {
                                console.log('Session cookie verified before retry', {
                                    path: originalRequest.url,
                                    cookieCheckAttempts,
                                    sessionCookiePreview: sessionCookieValue ? `${sessionCookieValue.substring(0, 20)}...` : null
                                });
                            }
                        }
                        // Update the request headers with the new token
                        originalRequest.headers['X-CSRF-Token'] = freshToken;
                        // Ensure the request will send cookies (withCredentials should already be true)
                        if (!originalRequest.withCredentials) {
                            originalRequest.withCredentials = true;
                        }
                        console.log('Retrying request with fresh CSRF token', {
                            path: originalRequest.url,
                            method: originalRequest.method,
                            tokenLength: freshToken.length,
                            hasSessionCookie: hasSessionCookie,
                            cookieCheckAttempts: cookieCheckAttempts > 0 ? cookieCheckAttempts : undefined
                        });
                        // Add a delay to ensure the session cookie is set before retrying
                        // Increased delay to account for slow networks and database operations
                        await new Promise(resolve => setTimeout(resolve, CSRF_RETRY_DELAY));
                        return axiosInstance(originalRequest);
                    }
                    else {
                        console.error('Failed to obtain fresh CSRF token for retry');
                    }
                }
                catch (tokenError) {
                    console.error('Error fetching CSRF token for retry:', {
                        error: tokenError?.message || String(tokenError),
                        path: originalRequest.url
                    });
                }
                // If we still don't have a token, log debugging info
                if (typeof document !== 'undefined') {
                    const cookies = document.cookie.split(';').map(c => c.trim());
                    const sessionCookie = cookies.find(c => c.startsWith('mypace.sid='));
                    const csrfCookie = cookies.find(c => c.startsWith('csrf-token='));
                    console.error('CSRF retry failed - cookie status:', {
                        hasSessionCookie: !!sessionCookie,
                        hasCSRFCookie: !!csrfCookie,
                        sessionCookiePreview: sessionCookie ? `${sessionCookie.substring(0, 20)}...` : null,
                        allCookies: cookies.length,
                        cookieDomain: typeof window !== 'undefined' ? window.location.hostname : 'unknown'
                    });
                }
            }
            else {
                console.error('CSRF retry already attempted, giving up', {
                    path: originalRequest.url,
                    method: originalRequest.method
                });
            }
        }
        console.error('Access forbidden to resource:', originalRequest.url);
        console.error('Request headers:', originalRequest.headers);
        console.error('Response data:', error.response.data);
        // Check if there's a session and if the token is still valid
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
            // Check token expiration (Supabase tokens include an exp claim)
            const tokenData = JSON.parse(atob(session.access_token.split('.')[1]));
            const expiresAt = tokenData.exp * 1000; // Convert to milliseconds
            const isExpired = Date.now() >= expiresAt;
            if (isExpired) {
                // Try refreshing again in case the 403 was due to an expired token
                const { data, error: refreshError } = await refreshSessionSingleFlight();
                if (!refreshError && data?.session) {
                    originalRequest.headers['Authorization'] = `Bearer ${data.session.access_token}`;
                    return axiosInstance(originalRequest);
                }
            }
        }
    }
    return rejectWithUserFacing(error);
});
// Add a debug helper to test token validation
export const testToken = async () => {
    try {
        // Get current token
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            console.error('No active session found');
            return { success: false, error: 'No active session' };
        }
        // Call our debug endpoint to test token validation
        const response = await axiosInstance.post('/debug/token-test', {
            token: session.access_token
        });
        return response.data;
    }
    catch (error) {
        console.error('Token test failed:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : String(error)
        };
    }
};
// Helper function to get current authenticated user ID
export const getCurrentUserId = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.user) {
            return session.user.id;
        }
        console.warn("No active session or user ID found");
        return null;
    }
    catch (error) {
        console.error('Error getting current user ID:', error);
        return null;
    }
};
/**
 * Checks if the current user is authenticated before proceeding with API calls
 *
 * @returns {Promise<{success: boolean, userId?: string, message?: string}>}
 */
export async function ensureProfileBeforeApiCall() {
    try {
        const userId = await getCurrentUserId();
        if (!userId) {
            console.error('User not authenticated');
            return { success: false, message: 'User not authenticated' };
        }
        return { success: true, userId };
    }
    catch (error) {
        console.error('Error checking authentication:', error);
        return { success: false, message: 'Error checking authentication' };
    }
}
// Export both default and named export for compatibility
export { axiosInstance };
export default axiosInstance;
